import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Typography,
  Link,
  Slide,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CookieIcon from "@mui/icons-material/Cookie";

const CookieBar = ({ onAccept, onReject }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Estado para controlar la visibilidad
  const [visible, setVisible] = useState(false);

  // Comprobar las cookies al montar el componente
  useEffect(() => {
    // Pequeño retraso para mejorar UX, no mostrar inmediatamente al cargar la página
    const timer = setTimeout(() => {
      const cookiesAccepted = localStorage.getItem("cookiesAccepted");
      if (cookiesAccepted !== "true" && cookiesAccepted !== "false") {
        setVisible(true);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Manejar aceptación de cookies
  const handleAccept = () => {
    setVisible(false);
    localStorage.setItem("cookiesAccepted", "true");

    // Llamar a callback si existe
    if (onAccept && typeof onAccept === "function") {
      onAccept();
    }
  };

  // Manejar rechazo de cookies
  const handleReject = () => {
    setVisible(false);
    localStorage.setItem("cookiesAccepted", "false");

    // Llamar a callback si existe
    if (onReject && typeof onReject === "function") {
      onReject();
    }
  };

  return (
    <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          backgroundColor: theme.palette.primary.main,
          color: "#FFFFFF",
          boxShadow: "0 -2px 5px rgba(0, 0, 0, 0.2)",
          zIndex: theme.zIndex.snackbar,
          py: 2.5, // Padding vertical para mejor espaciado
          px: { xs: 2, sm: 3 }, // Padding horizontal responsivo
        }}
        role="alert"
        aria-live="polite"
      >
        <Box
          sx={{
            maxWidth: "1200px",
            margin: "0 auto",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "center", sm: "flex-start" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "center", sm: "flex-start" },
              width: { xs: "100%", sm: "auto" },
              mb: { xs: 1, sm: 0 },
            }}
          >
            <CookieIcon sx={{ fontSize: "1.5rem", mr: 1 }} />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontFamily: "'Quicksand', sans-serif",
                fontSize: { xs: "1rem", sm: "1.2rem" },
                fontWeight: 600,
              }}
            >
              VicDown
            </Typography>
          </Box>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.9rem", sm: "1rem" },
              lineHeight: 1.5,
              textAlign: { xs: "center", sm: "left" },
              flex: 1,
              maxWidth: { xs: "100%", sm: "60%" },
            }}
          >
            {t("cookies.notice", "Usamos cookies para mejorar tu experiencia.")}
            <Link
              href="/cookies"
              sx={{
                color: theme.palette.secondary.light,
                ml: 1,
                fontWeight: 500,
                textDecoration: "underline",
                "&:hover": {
                  color: theme.palette.secondary.main,
                },
                "&:focus": {
                  outline: `2px solid ${theme.palette.secondary.main}`,
                  outlineOffset: "2px",
                },
              }}
              aria-label={t(
                "cookies.moreInfoAriaLabel",
                "Más información sobre nuestras cookies",
              )}
            >
              {t("cookies.moreInfo", "Más información")}
            </Link>
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              mt: { xs: 1, sm: 0 },
              flexDirection: { xs: "row", md: "row" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Button
              onClick={handleAccept}
              variant="contained"
              aria-label={t(
                "cookies.acceptAriaLabel",
                "Aceptar todas las cookies",
              )}
              fullWidth={isMobile}
              sx={{
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.secondary.contrastText,
                fontWeight: 600,
                padding: "8px 16px",
                fontSize: "0.9rem",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: theme.palette.secondary.dark,
                },
                "&:focus": {
                  outline: `2px solid ${theme.palette.primary.contrastText}`,
                  outlineOffset: "2px",
                },
              }}
            >
              {t("cookies.accept", "Aceptar")}
            </Button>
            <Button
              onClick={handleReject}
              variant="outlined"
              aria-label={t(
                "cookies.rejectAriaLabel",
                "Rechazar cookies opcionales",
              )}
              fullWidth={isMobile}
              sx={{
                borderColor: theme.palette.primary.contrastText,
                color: theme.palette.primary.contrastText,
                fontWeight: 600,
                padding: "8px 16px",
                fontSize: "0.9rem",
                borderRadius: "8px",
                "&:hover": {
                  borderColor: theme.palette.secondary.light,
                  color: theme.palette.secondary.light,
                  backgroundColor: "rgba(255,255,255,0.05)",
                },
                "&:focus": {
                  outline: `2px solid ${theme.palette.primary.contrastText}`,
                  outlineOffset: "2px",
                },
              }}
            >
              {t("cookies.reject", "Rechazar")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Slide>
  );
};

export default CookieBar;
